/**
 * MobileAppBanner — Smart app install prompt for mobile browser users.
 *
 * States:
 * 1. NOT logged in → soft banner: Create Account / Sign In / Continue in Browser
 *    (no app push — protect Stripe signup funnel)
 * 2. Logged in → stronger banner: Open App / Download App / Learn More / Continue in Browser
 * 3. Full-screen "Learn More" modal with bullet points
 * 4. Native app or desktop → hidden
 *
 * Dismissible & remembers choice for 14 days via localStorage.
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isNative } from "@/lib/capacitor";
import {
  isMobileBrowser,
  isBannerDismissed,
  dismissBanner,
  getStoreUrl,
  tryDeepLinkWithFallback,
} from "@/lib/mobileAppBanner";
import {
  Smartphone,
  X,
  ExternalLink,
  UserPlus,
  ArrowRight,
  Camera,
  ClipboardCheck,
  Navigation,
  PenTool,
  Zap,
  LogIn,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export function MobileAppBanner() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);

  useEffect(() => {
    if (isNative) return;
    if (!isMobileBrowser()) return;
    if (isBannerDismissed()) return;
    if (status === "loading") return;

    setVisible(true);
  }, [status]);

  // Hide on public tracking pages — customers don't need signup prompts
  if (location.pathname === "/track") return null;
  if (!visible) return null;

  const isLoggedIn = status === "authed";

  const handleDismiss = () => {
    dismissBanner();
    setVisible(false);
  };

  const handleOpenApp = () => {
    tryDeepLinkWithFallback();
  };

  const handleDownload = () => {
    window.location.href = getStoreUrl();
  };

  // ─── Full-screen "Learn More" modal ───
  if (showFullScreen) {
    return (
      <div className="fixed inset-0 z-[9999] bg-background flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground">TransportMate App</span>
          </div>
          <button
            onClick={() => setShowFullScreen(false)}
            className="p-1.5 rounded-full hover:bg-muted"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-8">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            Use TransportMate on the go
          </h2>

          <div className="space-y-4 mb-8">
            {[
              { icon: Camera, text: "Complete vehicle inspections with photos" },
              { icon: ClipboardCheck, text: "Capture proof of collection and delivery" },
              { icon: Navigation, text: "Get driver GPS tracking and live job updates" },
              { icon: PenTool, text: "Take customer signatures instantly" },
              { icon: Zap, text: "Manage jobs faster than mobile browser" },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex gap-3 items-center">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <p className="text-foreground text-sm font-medium">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-border space-y-3 pb-safe">
          <Button onClick={handleDownload} className="w-full" size="lg">
            <ExternalLink className="w-4 h-4 mr-2" />
            Download App
          </Button>
          <Button onClick={handleOpenApp} variant="outline" className="w-full" size="lg">
            <Smartphone className="w-4 h-4 mr-2" />
            Open App
          </Button>
          <button
            onClick={() => { handleDismiss(); setShowFullScreen(false); }}
            className="w-full text-center text-sm text-muted-foreground py-2"
          >
            Continue in browser
          </button>
        </div>
      </div>
    );
  }

  // ─── State 1: Unauthenticated — soft banner (no app push) ───
  if (!isLoggedIn) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[999] animate-in slide-in-from-bottom-4 duration-300">
        <div className="mx-3 mb-3 rounded-xl border border-border bg-card shadow-lg overflow-hidden relative">
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-muted z-10"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          <div className="p-4 pr-10">
            <p className="font-semibold text-foreground text-sm mb-1">
              New to TransportMate?
            </p>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              Create your account on the web to get started. Once you're set up,
              you can download the app for inspections, signatures and driver updates.
            </p>

            <div className="flex gap-2 mb-2">
              <Button
                size="sm"
                className="flex-1 text-xs"
                onClick={() => navigate("/auth")}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" />
                Create Account
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 text-xs"
                onClick={() => navigate("/auth")}
              >
                <LogIn className="w-3.5 h-3.5 mr-1" />
                Sign In
              </Button>
            </div>

            <button
              onClick={handleDismiss}
              className="w-full text-center text-xs text-muted-foreground py-1"
            >
              Continue in browser
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── State 2: Authenticated — stronger banner ───
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[999] animate-in slide-in-from-bottom-4 duration-300">
      <div className="mx-3 mb-3 rounded-xl border border-border bg-card shadow-lg overflow-hidden relative">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-muted z-10"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>

        <div className="p-4 pr-10">
          <p className="font-semibold text-foreground text-sm mb-1">
            Get the full TransportMate experience
          </p>
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            Use the mobile app for faster inspections, signatures, live driver
            tracking and job updates.
          </p>

          <div className="flex gap-2 mb-1">
            <Button size="sm" className="flex-1 text-xs" onClick={handleOpenApp}>
              <Smartphone className="w-3.5 h-3.5 mr-1" />
              Open App
            </Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={handleDownload}>
              <ExternalLink className="w-3.5 h-3.5 mr-1" />
              Download App
            </Button>
          </div>

          <div className="flex items-center justify-between mt-2">
            <button onClick={handleDismiss} className="text-xs text-muted-foreground py-1">
              Continue in browser
            </button>
            <button
              onClick={() => setShowFullScreen(true)}
              className="text-xs text-primary font-medium flex items-center gap-1 py-1"
            >
              Learn More
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileAppBanner;
